/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-controller-access-in-routes, ember/no-get, warp-drive/no-legacy-request-patterns */
import Route from '@ember/routing/route';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import PersonType from '@linc-technologies/linced-common/enums/person-type';
import { service } from '@ember/service';
import { alias } from '@ember/object/computed';
import { isEmpty } from '@ember/utils';
import { hash } from '@linc-technologies/linced-common/utils/promise';
import Staff from '@linc-technologies/linced-common/enums/roles/staff';
import moment from 'moment/moment';

export default class AttendanceRoute extends Route {
  // Services
  @service
  dates;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  terms;

  @alias('session.school.jurisdiction')
  jurisdiction;

  init() {
    super.init(...arguments);

    this.router.on('routeDidChange', (transition) => {
      if (transition.to.find((route) => route.name === this.routeName)) {
        this.controller.set('term', this.get('terms.currentOrNextTerm'));
      }
    });
  }

  model() {
    let person = this.modelFor('people/person').person;
    let schoolId = this.session.school.id;
    let academicYear = this.dates.currentAcademicYear.toString();
    let academicYearDates =
      this.dates.halfyearDatesForAcademicYear(academicYear);
    let dateFrom = academicYearDates.startDate.format('YYYY-MM-DD');
    let dateTo = academicYearDates.endDate.format('YYYY-MM-DD');
    let currentTerm = this.get('terms.currentTerm');
    let model = {
      person,
      enrolments: this.store.query('people/enrolment', { personId: person.id }),
      staff: this.store.query('person', { type: PersonType.STAFF }),
      terms: this.store.findAll('term'),
    };

    if (this.jurisdiction !== Jurisdiction.NZ_MOE) {
      model.dayCodes = this.store.query('attendance/nz-moe-day', {
        personId: person.id,
        dateFrom: dateFrom,
        dateTo: dateTo,
      });
    }
    if (this.jurisdiction === Jurisdiction.NZ_MOE) {
      model.responseActivityConfig = this.store.query(
        'attendance/response-activity-configuration',
        { schoolId },
      );
      model.attendancePage = this.store
        .query('posts/page', {
          isAttendance: true,
        })
        .then((pages) => {
          return pages[0];
        });
      model.principal = this.store
        .query('person', { type: PersonType.STAFF })
        .then((staff) => {
          return staff.find((person) => this.isPrincipal(person));
        });
      model.responseActivities = this.store.query(
        'attendance/response-activity',
        {
          personIds: JSON.stringify([person.id]),
          schoolId,
        },
      );
      model.currentTermThresholds = [];
      if (!isEmpty(currentTerm)) {
        model.currentTermThresholds = this.store.query('attendance/threshold', {
          personIds: JSON.stringify([person.id]),
          dateFrom: currentTerm.start,
          dateTo: moment().format('YYYY-MM-DD'),
        });
      }

      model.previousTermThresholds = [];
      if (!isEmpty(currentTerm) && !isEmpty(currentTerm.previous)) {
        model.previousTermThresholds = this.store.query(
          'attendance/threshold',
          {
            personIds: JSON.stringify([person.id]),
            dateFrom: currentTerm.previous.get('start'),
            dateTo: currentTerm.previous.get('end'),
          },
        );
      }
      model.termlyAbsences = model.terms.then((terms) => {
        let currentAcademicYear = this.dates.currentAcademicYear;
        let currentYearTerms = terms.filter((term) => {
          if (!term.start || !term.end) {
            return false;
          }

          return term.academicYear === currentAcademicYear.toString();
        });

        if (currentYearTerms.length === 0) {
          return;
        }

        return Promise.all(
          currentYearTerms.map((term) =>
            this.store
              .query('attendance/day-code', {
                dateFrom: term.start,
                dateTo: term.end,
                analysis: true,
                personIds: JSON.stringify([person.id]),
              })
              .then((dayCodes) => {
                let dayCode = dayCodes[0];
                return {
                  term: term.term,
                  absentWholeDays: dayCode ? dayCode.absentWholeDays : 0,
                };
              }),
          ),
        ).then((results) => results);
      });
    }

    return hash(model);
  }

  afterModel() {
    return this.terms.waitUntilLoaded();
  }

  isPrincipal(person) {
    if (isEmpty(person.roles)) {
      return false;
    }
    return person.roles.includes(Staff.PRINCIPAL);
  }

  resetController(controller) {
    if (!isEmpty(controller)) {
      controller.setProperties({
        model: null,
        records: [],
      });
    }
    // Unload all day codes from the store
    this.store.unloadAll('attendance/day-code');
  }
}
